import React, { useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import AnalyticsKPISection from "./AnalyticsKPISection";
import BmsFaultTrends from "./BmsFaultTrends";
import BmsFaultsInsights from "./BmsFaultsInsights";
import MostCriticalVehicles from "./MostCriticalVehicles";
import ChartDetailsView from "./ChartDetailsView";
import ChartCalendar from "../../DigitalTwinV3/ChartCalendar";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { BmsFaultsAnalyticsContainer } from "../Components/StyledComponents";
import { analyticsCalendarEndDate } from "../../../Helper/DatePicker/DateConverter";
import { useBaseEntityInfo } from "../../../Hooks/usePagesContent";

const BmsFaultsAnalytics = ({
    allFleets,
    allOnboardingLocationsHierarchy,
    startDate,
    endDate,
    onDateChange,
    vehiclesConnected,
    isLoadingVehiclesCount,
    showChartDetails,
    selectedChartType,
    selectedChartGranularity,
    onShowChartDetails,
    globalFilters,
    onGlobalFiltersChange,
}) => {

    const {baseEntityType, baseEntityTypeIDLable, baseEntityTypeLable, baseEntityTypePluralLable} = useBaseEntityInfo();

    // If details view is active, show only that
    if (showChartDetails && selectedChartType) {
        return (
            <BmsFaultsAnalyticsContainer>
                <ChartDetailsView
                    chartType={selectedChartType}
                    onBack={() => onShowChartDetails(false, null)}
                    startDate={startDate}
                    endDate={endDate}
                    initialGranularity={selectedChartGranularity}
                    allFleets={allFleets}
                    allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                    initialHierarchicalLocation={globalFilters?.hierarchicalLocation}
                    initialFleetIds={globalFilters?.fleets || []}
                    onboardingLocations={globalFilters?.onboardingLocations}
                    allOnboardingLocations={globalFilters?.allOnboardingLocations}
                    initialAlertCategories={globalFilters?.alertCategories}
                    initialOperationalStatus={globalFilters?.operationalStatus}
                    onGlobalFiltersChange={onGlobalFiltersChange}
                />
            </BmsFaultsAnalyticsContainer>
        );
    }

    return (
        <BmsFaultsAnalyticsContainer>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    {/* Date Filter and Vehicles Connected */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: 'space-between',
                            width: '100%',
                            gap: 2,
                        }}
                    >
                        <ChartCalendar
                            startDate={startDate}
                            endDate={endDate}
                            onDateChange={onDateChange}
                            disableAfterDate={analyticsCalendarEndDate()}
                            forcePresets={true}
                        />
                        {isLoadingVehiclesCount ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', height: '16px', padding: "8px 16px", }}>
                                <CartLoader />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    padding: "8px 16px",
                                    borderRadius: "8px",
                                }}
                            >
                                <Typography
                                    variant="fw400s16c000000"
                                    sx={{
                                        color: "#444B51",
                                        lineHeight: '16px',
                                    }}
                                >
                                    {baseEntityTypePluralLable} Connected :
                                </Typography>
                                <Typography
                                    variant="fw400s16c000000"
                                    sx={{
                                        fontWeight: 600,
                                        color: "#2E2E2E",
                                        lineHeight: '16px',
                                    }}
                                >
                                    {vehiclesConnected}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* KPI Section */}
                <AnalyticsKPISection startDate={startDate} endDate={endDate} globalFilters={globalFilters} vehiclesConnected={vehiclesConnected} />
            </Box>
            <Divider sx={{ my: 2, borderColor: '#E6E6E6' }} />

            <BmsFaultTrends
                onShowDetails={(chartType, granularity) => onShowChartDetails(true, chartType, granularity)}
                startDate={startDate}
                endDate={endDate}
                globalFilters={globalFilters}
            />

            <Divider sx={{ my: 2, borderColor: '#E6E6E6' }} />

            <BmsFaultsInsights
                startDate={startDate}
                endDate={endDate}
                globalFilters={globalFilters}
            />

            <Box sx={{ mt: 2 }}>
                <MostCriticalVehicles
                    startDate={startDate}
                    endDate={endDate}
                    globalFilters={globalFilters}
                />
            </Box>
        </BmsFaultsAnalyticsContainer>
    );
};

export default BmsFaultsAnalytics;
